import { html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import style from './Meter.css'

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/**
 * Meter is a graphical display of a numeric value within a known range,
 * such as disk usage, a battery level or a score. Unlike Progress Bar, it
 * represents a static measurement rather than the completion of a task.
 *
 * @status draft
 * @category feedback
 * @lightDom true
 *
 * @part track - The track which holds the indicator.
 * @part indicator - The filled portion of the track representing the value.
 *
 * @cssprop [--n-meter-size=var(--n-space-s)] - Controls the thickness of the meter, using our [spacing tokens](/tokens/#space).
 * @cssprop [--n-meter-border-radius=var(--n-border-radius-s)] - Controls how rounded the corners are, using [border radius tokens](/tokens/#border-radius).
 * @cssprop [--n-meter-color=var(--n-color-accent)] - Controls the color of the indicator, using [color tokens](/tokens/#color).
 */
@customElement('nord-meter')
export default class Meter extends DraftComponentMixin(LitElement) {
  /**
   * The current value. Must be between `min` and `max`.
   */
  @property({ reflect: true, type: Number }) value = 0

  /**
   * The minimum value. If `min` is greater than `max`, the two are swapped so
   * the range is always well-formed.
   */
  @property({ reflect: true, type: Number }) min = 0

  /**
   * The maximum value. If `min` is greater than `max`, the two are swapped so
   * the range is always well-formed.
   */
  @property({ reflect: true, type: Number }) max = 100

  /**
   * A label describing what the meter represents. Shown above the meter and
   * exposed to assistive technology.
   */
  @property() label = ''

  /**
   * Options used to format the displayed and announced value with
   * `Intl.NumberFormat`. When omitted, the value is shown as a percentage of
   * the range so the text stays in sync with the indicator fill.
   */
  @property({ attribute: false }) format?: Intl.NumberFormatOptions

  /**
   * The locale used by `Intl.NumberFormat` when formatting the value.
   * Defaults to the runtime locale.
   */
  @property() locale?: string

  /**
   * The effective range bounds, normalised so `min <= max` even if the
   * properties are set the other way round (a degenerate `min > max` would
   * otherwise produce NaN/negative fills).
   */
  private get bounds(): { min: number, max: number } {
    const min = Number.isNaN(this.min) ? 0 : this.min
    const max = Number.isNaN(this.max) ? min : this.max
    return min <= max ? { min, max } : { min: max, max: min }
  }

  /** The value clamped to the `min`/`max` range. */
  private get clampedValue(): number {
    const { min, max } = this.bounds
    return clamp(Number.isNaN(this.value) ? min : this.value, min, max)
  }

  /** The value as a percentage (0–100) of the range, clamped. */
  private get percentage(): number {
    const { min, max } = this.bounds
    const raw = ((this.clampedValue - min) / (max - min)) * 100
    return clamp(Number.isNaN(raw) ? 0 : raw, 0, 100)
  }

  /**
   * The human-readable value used for display and `aria-valuetext`. Always
   * derived from the clamped value so the announced/displayed text stays in
   * sync with the indicator fill (never raw or NaN).
   */
  private get formattedValue(): string {
    if (this.format) {
      return new Intl.NumberFormat(this.locale, this.format).format(this.clampedValue)
    }

    return new Intl.NumberFormat(this.locale, { style: 'percent' }).format(this.percentage / 100)
  }

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)
  }

  // Render into the light DOM so the role/value semantics are exposed directly
  // and assistive tech reads the label reliably (no shadow boundary).
  protected createRenderRoot() {
    return this
  }

  protected updated() {
    // Meter is a non-interactive measurement, so the ARIA lives on the host.
    const { min, max } = this.bounds
    this.setAttribute('role', 'meter')
    this.setAttribute('aria-valuenow', String(this.clampedValue))
    this.setAttribute('aria-valuemin', String(min))
    this.setAttribute('aria-valuemax', String(max))
    this.setAttribute('aria-valuetext', this.formattedValue)

    if (this.label) {
      this.setAttribute('aria-label', this.label)
    }
    else {
      this.removeAttribute('aria-label')
    }
  }

  render() {
    return html`
      ${this.label
        ? html`<div class="n-meter-header" aria-hidden="true">
            <span class="n-meter-label">${this.label}</span>
            <span class="n-meter-value">${this.formattedValue}</span>
          </div>`
        : nothing}
      <div class="n-meter-track" part="track">
        <div
          class="n-meter-indicator"
          part="indicator"
          style=${`inline-size: ${this.percentage}%`}
        ></div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-meter': Meter
  }
}
